import { createStore, applyMiddleware } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import thunk from "redux-thunk";
import rootReducer from "./reducers";
import { PERSIST_BLACKLIST, stripPersistBlacklistedKeys } from "./persistBlacklist";
import coeditBroadcastMiddleware from "./middleware/coeditBroadcastMiddleware";

const persistConfig = {
  key: "root",
  storage,
  // Slices excluded from persistence live in ./persistBlacklist (a leaf module
  // so the contract is unit-testable without importing the whole store). This
  // includes every modal/dialog visibility flag — a stuck-open dialog must
  // never survive a reload (see the Wheel-view "Translate" upsell case).
  blacklist: PERSIST_BLACKLIST,
  // `blacklist` above only stops these keys from being WRITTEN to storage
  // going forward — redux-persist still reads and rehydrates any of them
  // that are already sitting in localStorage["persist:root"] (stale data
  // from before a slice was blacklisted, or externally-injected), because
  // neither getStoredState nor the default stateReconciler consult the
  // blacklist. `migrate` runs on every REHYDRATE with the raw restored
  // state before it's merged into the store, so strip blacklisted keys
  // there too — see stripPersistBlacklistedKeys for the full explanation.
  migrate: (state) => Promise.resolve(stripPersistBlacklistedKeys(state)),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = createStore(
  persistedReducer,
  applyMiddleware(thunk, coeditBroadcastMiddleware)
);
const persistor = persistStore(store);

// EW-1004: fully remove the persisted redux state on logout so a shared-device
// next user cannot read the prior user's cached profile/subscription/referral/
// notifications data from localStorage["persist:root"]. Call this from every
// logout path. persistor.purge() is the redux-persist-blessed removal; the
// direct removeItem is a synchronous belt-and-suspenders in case the async
// purge write is still pending when the page navigates/reloads.
const purgePersistedState = async () => {
  try {
    await persistor.purge();
  } catch (e) {
    // ignore — fall through to the direct removal below
  }
  try {
    // persistConfig.key === "root" => storage key is `persist:root`.
    window.localStorage.removeItem(`persist:${persistConfig.key}`);
  } catch (e) {
    // localStorage unavailable (SSR / privacy mode) — nothing to purge
  }
};

export { store, persistor, purgePersistedState };
